import React, { useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';

import { useAppSelector } from 'store/hooks';
import { appliedFiltersSelector } from 'store/filter/filterSelector';
import type { IUser } from 'api/services/dataEndpoints';
import { useLazyGetUserSummaryQuery, useLazyGetUserActivityPlotQuery, useLazyGetUserByIdQuery } from 'api/services/dataEndpoints';
import { convertDateToServerStrFormat } from 'utils/timeHelper';
import TopSection from 'components/TopSection/TopSection';
import AuthContainer from 'components/AuthContainer/AuthContainer';
import type { DateKeyType } from 'types/commonTypes';
import UsersList from '../../components/UsersList/UsersList';
import SummaryTable from './components/SummaryTable';
import ActivityPlot from './components/ActivityPlot';
import { generateDateArray } from './utils/plotHelper';
import CustomTimeline from './components/CustomTimeline/CustomTimeline';
import GeneralStat from './components/GeneralStat/GeneralStat';
import CreateAuthLink from './components/CreateAuthLink/CreateAuthLink';
import UserInfo from './components/UserInfo/UserInfo';

export const UserActivity: React.FC = () => {
  const [
    triggerGetUserInfo,
    {
      data: userInfo,
      isError: isUserInfoError,
      isLoading: isUserInfoLoading,
    },
  ] = useLazyGetUserByIdQuery();
  const [
    triggerGetUserSummary,
    {
      data: userSummary,
      isError: isUserSummaryError,
      isLoading: isUserSummaryLoading,
    },
  ] = useLazyGetUserSummaryQuery();
  const [
    triggerUserActivityPlotQuery,
    {
      data: userActivityPlot,
      isError: isUserActivityPlotError,
      isLoading: isUserActivityPlotLoading,
    },
  ] = useLazyGetUserActivityPlotQuery();

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const filters = useAppSelector(appliedFiltersSelector);

  const handleSelectUser = (user: IUser | null) => {
    setSelectedUserId(user?.id || null);
  };

  const filteredActivity = useMemo(() => {
    if (!userSummary) return null;

    const dateArray = generateDateArray(
      userSummary.createdAt as DateKeyType,
      {
        from: filters.dateRange.from as DateKeyType || null,
        to: filters.dateRange.to as DateKeyType || null,
      },
    );

    const data = {
      dailyActivity: dateArray.map((date: DateKeyType) => ({
        day: date,
        volleys: userActivityPlot?.dailyStats?.[date] || 0,
      })),
      daysEnrolled: userSummary.daySinceFirstMessage,
      interactionsCount: userSummary.botMessagesCnt + userSummary.userMessagesCnt,
      quitPlanCompleted: userSummary.quitPlanFinished,
      startDate: userSummary.createdAt,
      userId: userSummary.userId,
    };

    return data;
  }, [userSummary, userActivityPlot, filters.dateRange.from, filters.dateRange.to]);

  React.useEffect(() => {
    if (selectedUserId) {
      triggerGetUserSummary(selectedUserId);
      triggerGetUserInfo(selectedUserId);
    }
  }, [selectedUserId, triggerGetUserSummary, triggerGetUserInfo]);

  React.useEffect(() => {
    if (selectedUserId) {
      triggerUserActivityPlotQuery({
        userId: selectedUserId,
        dateRange: {
          from: convertDateToServerStrFormat(filters.dateRange.from),
          to: convertDateToServerStrFormat(filters.dateRange.to),
        },
      });
    }
  }, [selectedUserId, triggerUserActivityPlotQuery, filters.dateRange.from, filters.dateRange.to]);

  return (
    <AuthContainer>
      <TopSection title="User Activity" />
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12}>
            <GeneralStat />
          </Grid>
          <Grid item xs={12} md={4}>
            <UsersList
              selectedUserId={selectedUserId}
              onUserSelect={handleSelectUser}
              userStatus={filters.userStatus}
            />
          </Grid>

          {!userSummary && !isUserInfoLoading &&
            (
            <Grid item>
              <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
              <Typography>Choose user</Typography>
              </Paper>
            </Grid>
            )
          }

          {userInfo && (
            <Grid item xs={12} md={8}>
              <UserInfo
                key={userInfo.id}
                data={userInfo}
              />
              <SummaryTable
                data={selectedUserId ? userSummary || null : null}
                isError={isUserSummaryError}
                isLoading={isUserSummaryLoading}
              />
              <CustomTimeline status={userSummary?.status} />
              <ActivityPlot
                userId={selectedUserId}
                data={selectedUserId ? filteredActivity || null : null}
                filters={{
                  dateRange: filters.dateRange,
                }}
                isError={isUserActivityPlotError}
                isLoading={isUserActivityPlotLoading}
              />
              {userSummary && <CreateAuthLink email={userSummary?.email} /> }
            </Grid>
          )}

        </Grid>
    </AuthContainer>
  );
};

export default UserActivity;
